import React, { useContext } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Home, Building2, Wrench, AlertTriangle } from 'lucide-react';
import { ContentContext } from '../App';

const iconMap = {
  Home,
  Building2,
  Wrench,
  AlertTriangle
};

const ServicesSection = () => {
  const { language } = useLanguage();
  const content = useContext(ContentContext);

  const services = content?.services || [];

  const getIcon = (iconName) => iconMap[iconName] || Home;

  return (
    <section id="services" className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-amber-400 font-semibold tracking-wide uppercase text-sm">
            {language === 'en' ? 'What We Offer' : '我们的服务'}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-2">
            {language === 'en' ? 'Our Services' : '服务项目'}
          </h2>
          <p className="text-slate-400 mt-4 text-lg">
            {language === 'en' ? 'Professional Roofing Solutions' : '专业屋顶解决方案'}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const Icon = getIcon(service.icon);
            return (
              <div
                key={service.id || index}
                className="group relative bg-slate-800 rounded-2xl overflow-hidden hover:transform hover:scale-[1.02] transition-all duration-300"
              >
                {/* Image */}
                <div className="h-48 overflow-hidden">
                  <img
                    src={service.image_url}
                    alt={language === 'en' ? service.title_en : service.title_zh}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
                </div>

                {/* Content */}
                <div className="relative p-6 -mt-12">
                  <div className="w-14 h-14 bg-amber-500 rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:bg-amber-400 transition-colors">
                    <Icon className="w-7 h-7 text-slate-900" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {language === 'en' ? service.title_en : service.title_zh}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {language === 'en' ? service.description_en : service.description_zh}
                  </p>
                </div>

                {/* Hover Border Effect */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-amber-500/30 rounded-2xl transition-colors" />
              </div>
            );
          })}
        </div>

        {/* CTA Banner */}
        <div className="mt-16 bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-8 md:p-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900">
                {language === 'en' ? 'New Customer Special - Up to 20% Off!' : '新客户特惠 - 最高享受8折！'}
              </h3>
              <p className="text-slate-800 mt-2">
                {language === 'en' 
                  ? 'Contact us today to claim your discount on all roofing services.'
                  : '立即联系我们，获取所有屋顶服务的优惠。'}
              </p>
            </div>
            <a
              href={`tel:${content?.company?.phone || '416-688-9988'}`}
              className="flex-shrink-0 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-8 py-4 rounded-xl transition-colors"
            >
              {language === 'en' ? `Call ${content?.company?.phone || '416-688-9988'}` : `致电 ${content?.company?.phone || '416-688-9988'}`}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
